import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import styled, { css, keyframes } from 'styled-components'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Checkbox from '@mui/material/Checkbox'
import ListItemText from '@mui/material/ListItemText'
import Tooltip from '@mui/material/Tooltip'
import CircularProgress from '@mui/material/CircularProgress'
import CheckIcon from '@mui/icons-material/Check'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { updateBpUnitInlineRoutine } from 'ducks/units/actions'
import { buildInlineUnitPatch, getInlineFieldValue } from 'utils/inlineUnitEdit'
import { getFloorOptions, getFloorsIntervals } from 'utils/bpData'

const SAVED_BADGE_MS = 1500

// Shared autosave state machine for one cell: idle -> saving -> saved | error.
// `save` returns 'invalid' | 'unchanged' | 'saving' so callers can decide
// whether to keep the draft value around.
const useInlineUnitSave = ({ bpId, unit, field }) => {
  const dispatch = useDispatch()
  const [status, setStatus] = useState('idle')
  const [error, setError] = useState('')
  const savedTimer = useRef(null)

  useEffect(() => () => clearTimeout(savedTimer.current), [])

  const reset = () => {
    setStatus('idle')
    setError('')
  }

  const save = (rawValue, { onReject } = {}) => {
    const result = buildInlineUnitPatch(field, rawValue, unit)

    if (result.error) {
      setStatus('error')
      setError(result.error)
      return 'invalid'
    }

    if (!result.changed) {
      reset()
      return 'unchanged'
    }

    clearTimeout(savedTimer.current)
    setStatus('saving')
    setError('')

    dispatch(
      updateBpUnitInlineRoutine({
        bpId,
        unitId: unit.id,
        patch: result.patch,
        onSuccess: () => {
          setStatus('saved')
          savedTimer.current = setTimeout(() => setStatus('idle'), SAVED_BADGE_MS)
        },
        onError: message => {
          setStatus('error')
          setError(message)
          typeof onReject === 'function' && onReject()
        }
      })
    )

    return 'saving'
  }

  return { status, error, save, reset }
}

const StatusMark = ({ status, error, offset }) => {
  if (status === 'saving') {
    return (
      <StatusSlot $offset={offset}>
        <CircularProgress size={14} thickness={5} />
      </StatusSlot>
    )
  }
  if (status === 'saved') {
    return (
      <StatusSlot $tone='ok' $offset={offset} aria-label='Saved'>
        <CheckIcon />
      </StatusSlot>
    )
  }
  if (status === 'error') {
    return (
      <Tooltip title={error || 'Could not save'} placement='top' arrow disableInteractive>
        <StatusSlot $tone='error' $offset={offset} aria-label={error || 'Could not save'}>
          <ErrorOutlineIcon />
        </StatusSlot>
      </Tooltip>
    )
  }
  return null
}

const ErrorText = ({ status, error }) =>
  status === 'error' && error ? <CellError role='alert'>{error}</CellError> : null

// Enter saves and jumps to the same column on the next row, so a whole
// column can be filled in top to bottom without touching the mouse.
const focusNextInColumn = (current, field) => {
  const inputs = Array.from(
    document.querySelectorAll(`[data-inline-field="${field}"]`)
  )
  const index = inputs.indexOf(current)
  const next = inputs[index + 1]
  if (next) {
    next.focus()
    typeof next.select === 'function' && next.select()
  }
}

export const InlineTextCell = ({
  bpId,
  unit,
  field,
  placeholder = '',
  inputMode,
  minWidth
}) => {
  const storeValue = getInlineFieldValue(unit, field)
  const [value, setValue] = useState(storeValue)
  const [dirty, setDirty] = useState(false)
  const { status, error, save, reset } = useInlineUnitSave({ bpId, unit, field })

  useEffect(() => {
    if (!dirty) setValue(storeValue)
  }, [storeValue, dirty])

  const commit = () => {
    if (!dirty) return
    const outcome = save(value, {
      onReject: () => {
        setValue(storeValue)
        setDirty(false)
      }
    })
    if (outcome !== 'invalid') setDirty(false)
  }

  const revert = () => {
    setValue(storeValue)
    setDirty(false)
    reset()
  }

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault()
      commit()
      focusNextInColumn(e.currentTarget, field)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      revert()
      e.currentTarget.blur()
    }
  }

  return (
    <CellWrapper $minWidth={minWidth} onClick={e => e.stopPropagation()}>
      <CellInput
        type='text'
        inputMode={inputMode}
        value={value}
        placeholder={placeholder}
        data-inline-field={field}
        data-unit-id={unit.id}
        aria-label={`${field} for ${unit.name || 'unit'}`}
        $status={status}
        onChange={e => {
          setValue(e.target.value)
          setDirty(true)
          if (status === 'error') reset()
        }}
        onBlur={commit}
        onKeyDown={handleKeyDown}
      />
      <StatusMark status={status} error={error} />
      <ErrorText status={status} error={error} />
    </CellWrapper>
  )
}

export const InlineFloorsCell = ({ bpId, bp, unit, minWidth }) => {
  const storeValue = getInlineFieldValue(unit, 'floorNumbers')
  const [value, setValue] = useState(storeValue)
  const [open, setOpen] = useState(false)
  const { status, error, save } = useInlineUnitSave({ bpId, unit, field: 'floorNumbers' })

  useEffect(() => {
    if (!open) setValue(storeValue)
  }, [storeValue.join('|'), open])

  const bpOptions = (bp ? getFloorOptions(bp, true) : []).map(option => ({
    label: String(option.label),
    value: String(option.value)
  }))
  // Floors pre-filled from public data can sit outside the building's declared
  // range. Keep them selectable so they can at least be unticked.
  const extraOptions = value
    .filter(floor => !bpOptions.some(option => option.value === floor))
    .map(floor => ({ label: getFloorsIntervals([floor])[0] || floor, value: floor }))
  const options = [...bpOptions, ...extraOptions]

  const handleClose = () => {
    setOpen(false)
    save(value, { onReject: () => setValue(storeValue) })
  }

  return (
    <CellWrapper $minWidth={minWidth} onClick={e => e.stopPropagation()}>
      <FloorsSelect
        multiple
        size='small'
        displayEmpty
        open={open}
        onOpen={() => setOpen(true)}
        onClose={handleClose}
        value={value}
        onChange={e => setValue(e.target.value)}
        inputProps={{ 'data-inline-field': 'floorNumbers', 'aria-label': `floors for ${unit.name || 'unit'}` }}
        renderValue={selected =>
          selected.length ? getFloorsIntervals(selected).join(', ') : <Placeholder>Select floors</Placeholder>
        }
        $status={status}
        MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
      >
        {options.map(option => (
          <MenuItem key={option.value} value={option.value} dense>
            <Checkbox size='small' checked={value.includes(option.value)} />
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </FloorsSelect>
      <StatusMark status={status} error={error} offset='30px' />
      <ErrorText status={status} error={error} />
    </CellWrapper>
  )
}

const colors = {
  darkNavy: '#0c132b',
  teal: '#36becd',
  lightBg5: '#cdd4ea',
  green: '#1f9d55',
  red: '#cd2000'
}

const pop = keyframes`
  from { transform: scale(0.6); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
`

const CellWrapper = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  min-width: ${({ $minWidth }) => $minWidth || '90px'};
  width: 100%;
`

const CellError = styled.span`
  flex-basis: 100%;
  margin-top: 4px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 11px;
  line-height: 1.2;
  color: ${colors.red};
`

const statusBorder = ({ $status }) => {
  if ($status === 'error') return colors.red
  if ($status === 'saved') return colors.green
  return colors.lightBg5
}

const CellInput = styled.input`
  box-sizing: border-box;
  width: 100%;
  height: 34px;
  padding: 0 28px 0 10px;
  border: 1px solid ${statusBorder};
  border-radius: 6px;
  background: #fff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: ${colors.darkNavy};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &::placeholder {
    color: ${colors.lightBg5};
    font-weight: 400;
  }

  &:focus {
    outline: none;
    border-color: ${colors.teal};
    box-shadow: 0 0 0 3px rgba(54, 190, 205, 0.18);
  }
`

const FloorsSelect = styled(Select)`
  width: 100%;

  .MuiSelect-select {
    padding: 6px 32px 6px 10px !important;
    min-height: 20px !important;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: ${colors.darkNavy};
  }

  .MuiOutlinedInput-notchedOutline {
    border-color: ${statusBorder};
    border-radius: 6px;
  }

  &.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${colors.teal} !important;
    border-width: 1px;
  }
`

const Placeholder = styled.span`
  color: ${colors.lightBg5};
  font-weight: 400;
`

const StatusSlot = styled.span`
  position: absolute;
  right: ${({ $offset }) => $offset || '8px'};
  top: 17px;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  pointer-events: ${({ $tone }) => ($tone === 'error' ? 'auto' : 'none')};
  animation: ${pop} 0.15s ease-out;

  svg {
    font-size: 16px;
  }

  ${({ $tone }) =>
    $tone === 'ok' &&
    css`
      color: ${colors.green};
    `}

  ${({ $tone }) =>
    $tone === 'error' &&
    css`
      color: ${colors.red};
      cursor: help;
    `}
`
